import { useContext, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/pro-duotone-svg-icons';

import { notificationContext } from '@context/notifications';

import type { Notification as NotificationType } from 'types';

import styles from './Notifications.module.scss';

const Notifications: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { notifications = [], hideNotification } = useContext(notificationContext);

  const uniqueNotifications = useMemo(
    () =>
      notifications.reduce((notifications, notification) => {
        if (notifications.find(({ id }) => !!id && id === notification.id)) return notifications;
        else return [...notifications, notification];
      }, [] as Array<NotificationType>),
    [notifications]
  );

  return (
    <motion.ul layout="position" className={styles['notifications']}>
      <AnimatePresence>
        {uniqueNotifications.map((notification) => (
          <motion.li
            className={styles['notification-container']}
            key={notification.id}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
          >
            <Notification {...notification} onHide={() => hideNotification(notification)} />
          </motion.li>
        ))}
      </AnimatePresence>
    </motion.ul>
  );
};

type NotificationProps = NotificationType & {
  onHide: (event: React.MouseEvent<HTMLButtonElement> | React.AnimationEvent<HTMLDivElement>) => void;
};

const Notification: React.FC<React.PropsWithChildren<NotificationProps>> = ({
  type,
  title,
  message,
  timeout = 1,
  size = 'regular',
  onHide,
  autoHideAfterTimeout = true,
}) => {
  const showNotificationIcon = type === 'warning' || type === 'error';

  return (
    <div className={styles['notification']} data-type={type} data-size={size}>
      <div className={styles['notification-content']} data-show-notification-icon={showNotificationIcon}>
        {showNotificationIcon && (
          <div className={styles['notification-icon']}>
            <FontAwesomeIcon icon={faExclamationCircle} />
          </div>
        )}

        <div className={styles['notification-title']}>{title}</div>
        {!!message && <div className={styles['notification-message']}>{message}</div>}
        <button className={styles['notification-hide-btn']} onClick={onHide}>
          Hide
        </button>
      </div>
      {autoHideAfterTimeout && (
        <div
          className={styles['notification-countdown']}
          style={{ ['--countdown-time' as string]: timeout / 1000 }}
        >
          <div className={styles['notification-countdown-bar']} onAnimationEnd={onHide} />
        </div>
      )}
    </div>
  );
};

export default Notifications;
